// The library (W2 on the API): your own shelf first (works you've added, where you left off, and the
// collections you've named, all synced), then every Bible, then the books, confessions, catechisms and
// commentaries. Each entry says plainly what it is to you: free to read, in your library, to get, in the
// app, or not available yet (a rights switch is off). No prices, no badges.
//
// The books stand in a bookcase by default (iOS FS-371, BS-WEB-FEAT-4): spines out on wooden shelves,
// each drawn from what the book is. The reader can lay them out as covers or as a list instead, and the
// books travel to their new places (a View Transition; a cut under reduced motion). The choice is kept
// in this browser.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { BookCover } from "../../components/BookCover";
import { Bookcase, type CaseBook } from "../../components/bookcase/Bookcase";
import { BookcaseSkeleton } from "../../components/bookcase/BookcaseSkeleton";
import { rails as caseRails } from "../../components/bookcase/rails";
import { find, missLine, terms, type Findable, type Pill } from "../../components/library/find";
import { barBottom, LibraryBar } from "../../components/library/LibraryBar";
import { Skeleton } from "../../components/Skeleton";
import { LibraryHead, type Counts, type Reading } from "../../components/library/LibraryHead";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/Empty";
import { Input } from "../../components/Field";
import { Icon } from "../../components/Icon";
import { shelfStyles as sh } from "../../components/library/Shelf";
import { SectionHeader } from "../../components/SectionHeader";
import { SegmentedControl } from "../../components/Tabs";
import ui from "../../components/ui.module.css";
import { ACCESS_LINE, accessState, LANGUAGE, useEntitlements, type AccessState, type LibraryEntry } from "../../data/catalogueSource";
import { usePurchasesOpen } from "../../data/queries";
import { bookByOsis } from "../../data/books";
import { toneOf, useAuthors, useCatalogue, type Author, type Catalogue, type Work } from "../../data/works";
import { morphFrom } from "../../motion/shared";
import { useTitle } from "../../lib/useTitle";
import { asPosition, asShelfCollection, asShelfItem, newCollection, type ShelfCollection } from "../../sync/domain";
import { useCollection } from "../../sync/react";
import { useChange } from "../../reader/useMarks";
import s from "./library.module.css";

export const Route = createFileRoute("/_app/library/")({
  validateSearch: (q: Record<string, unknown>): { checkout?: "cancelled"; shelf?: string } => ({
    ...(q.checkout === "cancelled" ? { checkout: "cancelled" as const } : {}),
    ...(typeof q.shelf === "string" && q.shelf.length < 40 ? { shelf: q.shelf } : {}),
  }),
  component: Library,
});

const KINDS = ["book", "confession", "catechism", "commentary"] as const;
const KIND_HEAD: Record<(typeof KINDS)[number], string> = { book: "Books", confession: "Confessions", catechism: "Catechisms", commentary: "Commentaries" };

type View = "shelves" | "covers" | "list";
const VIEWS: { value: View; label: string }[] = [
  { value: "shelves", label: "Shelves" },
  { value: "covers", label: "Covers" },
  { value: "list", label: "List" },
];
const VIEW_KEY = "bs.library.view";

function readView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "covers" || v === "list" ? v : "shelves";
  } catch {
    return "shelves";
  }
}

type StateOf = (w: { entry: LibraryEntry; text?: boolean }) => AccessState;

/** One empty list for every render, so a memo keyed on it holds while the reader is signed out. */
const NONE: never[] = [];

function Library() {
  useTitle("Library");
  const { checkout, shelf: shelfFilter } = Route.useSearch();
  const { data: catalogue, isPending, isError, refetch } = useCatalogue();
  const { data: ents } = useEntitlements();
  const purchases = usePurchasesOpen();
  const [view, setView] = useState<View>(readView);
  const books = useRef<HTMLDivElement | null>(null);
  const [pill, setPill] = useState<Pill>("all");
  const [query, setQuery] = useState<string | null>(null);
  const placesQuery = useCollection("readingPosition");
  const places = placesQuery.data ?? NONE;
  const reading = useMemo(() => (catalogue ? readingNow(catalogue, places) : null), [catalogue, places]);
  // The head waits for the reader's places (when there is a reader) so it never shows the empty head first.
  const headReady = !!catalogue && !(placesQuery.isPending && placesQuery.fetchStatus !== "idle");
  // The bookcase arrives once a visit (BS-IOS-FEAT-587), not again on a view switch or after the find.
  const arrivalRef = useRef(false);
  const counts = useMemo(() => countsOf(catalogue), [catalogue]);

  /**
   * A pill (FS-371 D5): on the shelf it dims the books outside it and jumps to its first rail, landing
   * under the pinned row; on the covers and the list it shows its part. All, or the chosen pill again,
   * goes back to the top.
   */
  const pick = (p: Pill, again = false) => {
    setPill(p);
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior: ScrollBehavior = still ? "auto" : "smooth";
    requestAnimationFrame(() => {
      const target = document.querySelector(`[data-jump="${p}"]`);
      if (p === "all" || again || !target) return window.scrollTo({ top: 0, behavior });
      // Measured, so it lands just under the pinned row however tall the row stands (it wraps on a phone).
      const y = target.getBoundingClientRect().top;
      window.scrollTo({ top: Math.max(0, window.scrollY + y - barBottom(y > 0) - 8), behavior });
    });
  };

  const state: StateOf = (w) => accessState(w.entry, ents, { purchases, webText: w.text });
  /** What `state` reads, so the bookcase stands its books again only when one of them changes. */
  const stateKey = `${purchases}|${JSON.stringify(ents ?? null)}`;

  /** Lay the books out anew; each cover travels to its new place. */
  const choose = (v: View) => {
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private mode: the choice lasts this visit */
    }
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const doc = document as Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };
    if (still || !doc.startViewTransition || !books.current) {
      setView(v);
      return;
    }
    const root = document.documentElement;
    // A switch is not a visit: the books travel to their places and do not arrive again.
    arrivalRef.current = true;
    root.dataset.vt = "layout";
    books.current.dataset.switching = "";
    const t = doc.startViewTransition(() => flushSync(() => setView(v)));
    void t.finished.finally(() => {
      delete root.dataset.vt;
      if (books.current) delete books.current.dataset.switching;
    });
  };

  // A query of only punctuation or spaces asks for nothing (iOS `isEmptyQuery`): the library stands.
  const finding = query !== null && terms(query).length > 0;
  return (
    <div className={ui.pageWide}>
      <LibraryHead
        title={<h1 className={ui.title}>Library</h1>}
        reading={reading}
        counts={counts}
        ready={headReady}
        onPick={(p) => pick(p)}
        purchases={purchases && !!ents?.length}
      />
      <LibraryBar
        pill={pill}
        onPill={pick}
        query={query}
        onQuery={setQuery}
        view={<SegmentedControl label="Show the books as" options={VIEWS} value={view} onChange={choose} size="sm" />}
      />

      <FindStatus catalogue={catalogue} query={query} pill={pill} />

      {checkout === "cancelled" && (
        <p className={s.notice} role="status">
          The purchase was cancelled. Nothing was charged.
        </p>
      )}

      {isError ? (
        <EmptyState title="The library isn’t available just now." body="Check your connection, then try again.">
          <Button onClick={() => void refetch()}>Try again</Button>
        </EmptyState>
      ) : isPending || !catalogue ? (
        view === "shelves" ? (
          <BookcaseSkeleton />
        ) : (
          <div className={s.loading} role="status">
            <span className="visually-hidden">Loading the library</span>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} shape="block" width={132} height={211} />
            ))}
          </div>
        )
      ) : finding ? (
        <FindResults catalogue={catalogue} query={query!} pill={pill} state={state} />
      ) : (
        <div ref={books} className={s.books} data-view={view}>
          {view === "shelves" ? (
            <Shelves catalogue={catalogue} state={state} stateKey={stateKey} filter={shelfFilter} pill={pill} arrivalRef={arrivalRef} />
          ) : (
            <>
              {pill === "all" && <MyLibrary works={catalogue.works} bibles={catalogue.bibles} state={state} filter={shelfFilter} view={view} />}
              {pill !== "books" && <Bibles bibles={catalogue.bibles} state={state} />}
              {KINDS.map((k) => {
                if (pill === "bibles") return null;
                const list = catalogue.works.filter((w) => w.kind === k);
                if (!list.length) return null;
                return <KindSection key={k} kind={k} list={list} sets={catalogue.sets} state={state} view={view} />;
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** What the reader is reading now: the newest place in a work or a Bible the library holds. */
function readingNow(catalogue: Catalogue, places: Parameters<typeof asPosition>[0][]): Reading | null {
  const latest = places
    .map(asPosition)
    .filter((p) => !!p)
    .sort((a, b) => b!.readAt - a!.readAt);
  for (const p of latest) {
    const bible = catalogue.bibles.find((b) => b.id === p!.libraryId);
    if (bible) {
      const [bk, ch] = p!.anchor?.verseKey?.split(".") ?? [];
      return { id: bible.id, title: bible.title, shortTitle: bible.shortTitle, place: bk && ch ? `${bookByOsis(bk)?.ref ?? bk} ${ch}` : null, progress: null, bible: true, link: { to: "/bible/$id", params: { id: bible.id } } };
    }
    const work = catalogue.works.find((w) => w.id === p!.libraryId);
    if (work) {
      const section = p!.anchor?.sectionUid;
      const pct = p!.progress === null ? null : Math.max(1, Math.round(p!.progress * 100));
      return {
        id: work.id,
        title: work.title,
        shortTitle: work.shortTitle,
        author: work.author,
        place: pct === null ? null : `${pct}% through`,
        progress: p!.progress,
        bible: false,
        link: section ? { to: "/books/$id/$section", params: { id: work.id, section } } : { to: "/books/$id", params: { id: work.id } },
      };
    }
  }
  return null;
}

/** The library's counts: its books and their authors, its Bibles and their languages. */
function countsOf(catalogue: Catalogue | undefined): Counts {
  if (!catalogue) return { books: 0, authors: 0, bibles: 0, languages: 0 };
  return {
    books: catalogue.works.length,
    authors: new Set(catalogue.works.map((w) => w.author).filter(Boolean)).size,
    bibles: catalogue.bibles.length,
    languages: new Set(catalogue.bibles.map((b) => b.language ?? "")).size,
  };
}

/**
 * The find's matches (iOS BUG-441): a page of covers under "Matches for “q” · n", the pill's part of the
 * library only; the miss waits 0.15 s (`findMissDelay`) so a quick answer never flashes it.
 */
function FindResults({ catalogue, query, pill, state }: { catalogue: Catalogue; query: string; pill: Pill; state: StateOf }) {
  const { data: authors } = useAuthors();
  const pool = useMemo<(Findable & { work?: Work; bible?: LibraryEntry })[]>(
    () => [
      ...catalogue.bibles.map((b) => ({ id: b.id, kind: "bible", title: b.title, shortTitle: b.shortTitle, language: LANGUAGE[b.language ?? ""] ?? b.language, bible: b })),
      ...catalogue.works.map((w) => {
        const set = catalogue.sets.find((x) => x.members.includes(w.id));
        return { id: w.id, kind: w.kind, title: w.title, shortTitle: w.shortTitle, author: w.author, set: set ? { title: set.title, number: set.members.indexOf(w.id) + 1 } : null, work: w };
      }),
    ],
    [catalogue],
  );
  const matches = find(pool, query, pill);
  const q = query.trim();
  return (
    <section className={s.section} aria-labelledby="find-head">
      <h2 id="find-head" className={s.findHead}>
        Matches for “{q}” · {matches.length}
      </h2>
      {matches.length ? (
        <ul className={s.grid}>
          {matches.map((m) =>
            m.work ? (
              <li key={m.id}>
                <BookLink work={m.work} st={state(m.work)} tone={toneOf(m.work, authors)} />
              </li>
            ) : m.bible ? (
              <li key={m.id}>
                {state({ entry: m.bible }) === "unavailable" ? (
                  <span className={s.cover}>
                    <BookCover id={m.bible.id} title={m.bible.title} shortTitle={m.bible.shortTitle} width={132} caption dimmed />
                  </span>
                ) : (
                  <Link to="/bible/$id" params={{ id: m.bible.id }} className={s.cover}>
                    <BookCover id={m.bible.id} title={m.bible.title} shortTitle={m.bible.shortTitle} width={132} caption />
                  </Link>
                )}
              </li>
            ) : null,
          )}
        </ul>
      ) : (
        <p className={s.miss}>{missLine(q, pill)}</p>
      )}
    </section>
  );
}

/**
 * The find's one status line for assistive tech, standing while the find is open and spoken a moment
 * after the typing stops: the count, or the miss.
 */
function FindStatus({ catalogue, query, pill }: { catalogue: Catalogue | undefined; query: string | null; pill: Pill }) {
  const [said, setSaid] = useState("");
  useEffect(() => {
    if (query === null || !catalogue) return;
    const t = setTimeout(() => {
      if (!terms(query).length) return setSaid("");
      const pool: Findable[] = [
        ...catalogue.bibles.map((b) => ({ id: b.id, kind: "bible", title: b.title, shortTitle: b.shortTitle, language: LANGUAGE[b.language ?? ""] ?? b.language })),
        ...catalogue.works.map((w) => {
          const set = catalogue.sets.find((x) => x.members.includes(w.id));
          return { id: w.id, kind: w.kind, title: w.title, shortTitle: w.shortTitle, author: w.author, set: set ? { title: set.title, number: set.members.indexOf(w.id) + 1 } : null };
        }),
      ];
      const n = find(pool, query, pill).length;
      setSaid(n ? `${n} ${n === 1 ? "match" : "matches"}` : missLine(query, pill));
    }, 450);
    return () => clearTimeout(t);
  }, [catalogue, query, pill]);
  if (query === null) return null;
  return (
    <p className="visually-hidden" role="status">
      {said}
    </p>
  );
}

/** A work as the bookcase stands it. */
function caseWork(w: Work, st: AccessState, authors: Author[] | undefined): CaseBook {
  return {
    id: w.id,
    kind: w.kind,
    title: w.title,
    shortTitle: w.shortTitle,
    spineTitle: w.spineTitle,
    author: w.author,
    binding: w.binding,
    language: w.language,
    words: w.words,
    tone: toneOf(w, authors),
    state: ACCESS_LINE[st],
    away: away(st),
    ribbon: st === "get",
    link: { to: "/books/$id", params: { id: w.id } },
  };
}

/** A Bible as the bookcase stands it: it opens where the reader left off, once its rights are cleared. */
function caseBible(b: LibraryEntry, st: AccessState): CaseBook {
  const open = st !== "unavailable";
  return {
    id: b.id,
    kind: "bible",
    title: b.title,
    shortTitle: b.shortTitle,
    binding: b.binding,
    language: b.language,
    words: b.words ?? null,
    state: open ? "Opens where you left off" : ACCESS_LINE.unavailable,
    away: !open,
    link: open ? { to: "/bible/$id", params: { id: b.id } } : null,
  };
}

/** Bibles in English first, then by language, then by title. */
function bibleOrder(bibles: LibraryEntry[]): LibraryEntry[] {
  const lang = (b: LibraryEntry) => LANGUAGE[b.language ?? ""] ?? b.language ?? "Other";
  return [...bibles].sort((a, z) => (lang(a) === lang(z) ? a.title.localeCompare(z.title) : lang(a) === "English" ? -1 : lang(z) === "English" ? 1 : lang(a).localeCompare(lang(z))));
}

/** The bookcase: the reader's own shelf and places as its first rails, then the whole library. */
function Shelves({ catalogue, state, stateKey, filter, pill, arrivalRef }: { catalogue: Catalogue; state: StateOf; stateKey: string; filter?: string; pill: Pill; arrivalRef: { current: boolean } }) {
  const { data: shelf = NONE } = useCollection("shelf");
  const { data: places = NONE } = useCollection("readingPosition");
  const { data: authors } = useAuthors();
  const rails = useMemo(
    () => {
      const work = new Map(catalogue.works.map((w) => [w.id, w]));
      const bible = new Map(catalogue.bibles.map((b) => [b.id, b]));
      const book = (id: string): CaseBook | null => {
        const w = work.get(id);
        if (w) return caseWork(w, state(w), authors);
        const b = bible.get(id);
        return b ? caseBible(b, state({ entry: b })) : null;
      };
      const mine = shelf
        .map(asShelfItem)
        .filter((i) => !!i && !i.hidden && (!filter || i.collectionIds.includes(filter)))
        .map((i) => book(i!.libraryId))
        .filter((b): b is CaseBook => !!b);
      const seen = new Set<string>();
      // Recent opens each book where the reader left it (iOS's Recent), a work at its section.
      const recent = places
        .map(asPosition)
        .filter((p) => !!p)
        .sort((a, b) => b!.readAt - a!.readAt)
        .filter((p) => !seen.has(p!.libraryId) && !!seen.add(p!.libraryId))
        .map((p): CaseBook | null => {
          const b = book(p!.libraryId);
          const section = p!.anchor?.sectionUid;
          return b && b.link?.to === "/books/$id" && section ? { ...b, link: { to: "/books/$id/$section", params: { id: b.id, section } } } : b;
        })
        .filter((b): b is CaseBook => !!b);
      return caseRails({
        mine,
        recent,
        bibles: bibleOrder(catalogue.bibles).map((b) => caseBible(b, state({ entry: b }))),
        works: catalogue.works.map((w) => caseWork(w, state(w), authors)),
        sets: catalogue.sets,
      });
    },
    // `state` is new each render; `stateKey` is what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogue, shelf, places, authors, filter, stateKey],
  );
  return (
    <>
      <MyLibrary works={catalogue.works} bibles={catalogue.bibles} state={state} filter={filter} view="shelves" />
      {rails.length ? (
        <Bookcase rails={rails} sets={catalogue.sets} describedBy="rights-note" pill={pill} arrivalRef={arrivalRef} />
      ) : (
        <EmptyState title="Nothing stands in the library yet." body="The books will be here as soon as the library is." />
      )}
      {catalogue.bibles.some((b) => !b.available) && (
        <p id="rights-note" className={s.note}>
          Editions marked “not available yet” are waiting for their rights to be cleared. They’ll open here as soon as they are.
        </p>
      )}
    </>
  );
}

type SetGroup = { kind: "set"; id: string; title: string; author: string; works: Work[] };
type Group = { kind: "work"; work: Work } | SetGroup;

/** Works in a registry set (Owen's 23 volumes) as one entry that opens; every other work as itself, in order. */
function groupSets(list: Work[], sets: { id: string; title: string; author: string; members: string[] }[]): Group[] {
  const out: Group[] = [];
  const seen = new Set<string>();
  for (const w of list) {
    const set = sets.find((x) => x.members.includes(w.id));
    const members = set ? list.filter((x) => set.members.includes(x.id)) : [];
    if (!set || members.length < 3) {
      out.push({ kind: "work", work: w });
      continue;
    }
    if (seen.has(set.id)) continue;
    seen.add(set.id);
    out.push({ kind: "set", id: set.id, title: set.title, author: set.author, works: members.sort((a, b) => set.members.indexOf(a.id) - set.members.indexOf(b.id)) });
  }
  return out;
}

/** A set's line: free if any volume is, owned if all are, else what most of it is. */
function setState(states: AccessState[]): AccessState {
  if (states.every((x) => x === "owned")) return "owned";
  if (states.includes("get")) return "get";
  if (states.every((x) => x === "unavailable")) return "unavailable";
  return states.find((x) => x !== "free" && x !== "owned") ?? "free";
}

const away = (st: AccessState) => st === "unavailable" || st === "app" || st === "reference";

function StateLine({ st }: { st: AccessState }) {
  return <span className={st === "free" || st === "owned" ? s.state : st === "get" ? s.stateGet : s.stateQuiet}>{ACCESS_LINE[st]}</span>;
}

type Laid = "covers" | "list";

/** One kind of work (Books, Catechisms…) as covers or a list. A set opens below its cover. */
function KindSection({ kind, list, sets, state, view }: { kind: (typeof KINDS)[number]; list: Work[]; sets: Parameters<typeof groupSets>[1]; state: StateOf; view: Laid }) {
  const { data: authors } = useAuthors();
  const [open, setOpen] = useState<string | null>(null);
  const groups = groupSets(list, sets);
  const headId = `kind-${kind}`;
  const openSet = groups.find((g): g is SetGroup => g.kind === "set" && g.id === open);

  if (view === "list")
    return (
      <section className={s.section} aria-labelledby={headId} data-jump="books">
        <SectionHeader id={headId} title={KIND_HEAD[kind]} />
        <ul className={s.rows}>
          {groups.map((g) =>
            g.kind === "work" ? (
              <WorkRow key={g.work.id} work={g.work} state={state(g.work)} tone={toneOf(g.work, authors)} vt={`lib-${kind}-${g.work.id}`} />
            ) : (
              <li key={g.id}>
                <details className={s.set}>
                  <summary className={s.setSummary}>
                    <span className={s.vt} style={{ ["--vt" as string]: `lib-${kind}-${g.works[0]!.id}` }}>
                      <BookCover id={g.works[0]!.id} title={g.title} author={g.author} tone={toneOf(g.works[0], authors)} width={40} decorative />
                    </span>
                    <span className={s.rowText}>
                      <span className={s.name}>{g.title}</span>
                      <span className={s.meta}>
                        {g.author}, {g.works.length} volumes
                      </span>
                      <StateLine st={setState(g.works.map((w) => state(w)))} />
                    </span>
                  </summary>
                  <ul className={s.setList}>
                    {g.works.map((w) => (
                      <WorkRow key={w.id} work={w} state={state(w)} tone={toneOf(w, authors)} />
                    ))}
                  </ul>
                </details>
              </li>
            ),
          )}
        </ul>
      </section>
    );

  const slots = groups.map((g) => {
    if (g.kind === "work") {
      return (
        <li key={g.work.id}>
          <BookLink work={g.work} st={state(g.work)} tone={toneOf(g.work, authors)} vt={`lib-${kind}-${g.work.id}`} />
        </li>
      );
    }
    const st = setState(g.works.map((w) => state(w)));
    const first = g.works[0]!;
    const isOpen = open === g.id;
    return (
      <li key={g.id}>
        <button type="button" className={s.cover} aria-expanded={isOpen} aria-controls={isOpen ? `set-${g.id}` : undefined} onClick={() => setOpen(isOpen ? null : g.id)}>
          <span className={s.vt} style={{ ["--vt" as string]: `lib-${kind}-${first.id}` }}>
            <BookCover id={first.id} title={g.title} author={g.author} tone={toneOf(first, authors)} width={132} dimmed={away(st)} decorative />
          </span>
          <SetCaption title={g.title} count={g.works.length} st={st} open={isOpen} />
        </button>
      </li>
    );
  });

  return (
    <section className={s.section} aria-labelledby={headId} data-jump="books">
      <SectionHeader id={headId} title={KIND_HEAD[kind]} />
      <ul className={s.grid}>{slots}</ul>
      {openSet && (
        <div id={`set-${openSet.id}`} className={s.opened}>
          <ul className={s.grid}>
            {openSet.works.map((w) => (
              <li key={w.id}>
                <BookLink work={w} st={state(w)} tone={toneOf(w, authors)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function SetCaption({ title, count, st, open }: { title: string; count: number; st: AccessState; open: boolean }) {
  return (
    <span className={s.captionWords}>
      <span className={sh.title}>{title}</span>
      <span className={sh.meta}>{open ? "Open below" : `${count} volumes`}</span>
      <StateLine st={st} />
    </span>
  );
}

/** A work as its cover: its link names the work and what it is to you. */
function BookLink({ work, st, tone, vt }: { work: Work; st: AccessState; tone?: string; vt?: string }) {
  return (
    <Link to="/books/$id" params={{ id: work.id }} className={s.cover} onClick={morphFrom("book", work.id)}>
      <span className={s.vt} style={vt ? { ["--vt" as string]: vt } : undefined} data-morph="book">
        <BookCover id={work.id} title={work.title} shortTitle={work.shortTitle} author={work.author} tone={tone} width={132} dimmed={away(st)} decorative />
        {st === "get" && <span className={s.ribbon} aria-hidden="true" />}
      </span>
      <span className={s.captionWords}>
        <span className={sh.title}>{work.title}</span>
        {work.author && <span className={sh.meta}>{work.author}</span>}
        <StateLine st={st} />
      </span>
    </Link>
  );
}

function WorkRow({ work, state, tone, vt }: { work: Work; state: AccessState; tone?: string; vt?: string }) {
  return (
    <li>
      <Link to="/books/$id" params={{ id: work.id }} className={s.row} onClick={morphFrom("book", work.id)}>
        <span className={s.vt} style={vt ? { ["--vt" as string]: vt } : undefined} data-morph="book">
          <BookCover id={work.id} title={work.title} shortTitle={work.shortTitle} author={work.author} tone={tone} width={40} decorative dimmed={away(state)} />
        </span>
        <span className={s.rowText}>
          <span className={s.name}>{work.title}</span>
          <span className={s.meta}>{[work.author, work.written].filter(Boolean).join(", ")}</span>
          <StateLine st={state} />
        </span>
        <Icon name="forward" size={18} />
      </Link>
    </li>
  );
}

function Bibles({ bibles, state }: { bibles: LibraryEntry[]; state: StateOf }) {
  const groups = useMemo(() => {
    const m = new Map<string, LibraryEntry[]>();
    for (const b of bibles) {
      const lang = LANGUAGE[b.language ?? ""] ?? b.language ?? "Other";
      m.set(lang, [...(m.get(lang) ?? []), b]);
    }
    return [...m.entries()].sort(([a], [b]) => (a === "English" ? -1 : b === "English" ? 1 : a.localeCompare(b)));
  }, [bibles]);
  return (
    <section className={s.section} aria-labelledby="bibles-head" data-jump="bibles">
      <SectionHeader id="bibles-head" title="Bibles" />
      <div className={s.languages}>
        {groups.map(([lang, list]) => (
          <div key={lang} className={s.language}>
            <h3 className={s.languageName}>{lang}</h3>
            <ul className={s.bibleList}>
              {list.map((b) => {
                const st = state({ entry: b });
                return (
                  <li key={b.id}>
                    {st === "unavailable" ? (
                      <span className={s.bible} aria-describedby="rights-note">
                        <span className={s.leather} aria-hidden="true" data-off="">
                          <span>{b.shortTitle}</span>
                        </span>
                        <span className={s.rowText}>
                          <span className={s.bibleName}>{b.title}</span>
                          <span className={s.stateQuiet}>Not available yet</span>
                        </span>
                      </span>
                    ) : (
                      <Link to="/bible/$id" params={{ id: b.id }} className={s.bible}>
                        <span className={s.leather} aria-hidden="true">
                          <span>{b.shortTitle}</span>
                        </span>
                        <span className={s.rowText}>
                          <span className={s.bibleName}>{b.title}</span>
                          <span className={s.meta}>Opens where you left off</span>
                        </span>
                        <Icon name="forward" size={18} />
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {bibles.some((b) => !b.available) && (
        <p id="rights-note" className={s.note}>
          Editions marked “not available yet” are waiting for their rights to be cleared. They’ll open here as soon as they are.
        </p>
      )}
    </section>
  );
}

/**
 * The reader's own shelf and places, synced: what they've added, where they left off, their collections.
 * On the bookcase only its heading, its collections and its notes stand here; the books themselves stand
 * on the case's first rails (My library, Recent).
 */
function MyLibrary({ works, bibles, state, filter, view }: { works: Work[]; bibles: LibraryEntry[]; state: StateOf; filter?: string; view: View }) {
  const { data: shelf = [], isPending } = useCollection("shelf");
  const { data: places = [] } = useCollection("readingPosition");
  const { data: authors } = useAuthors();
  const change = useChange();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  const items = shelf.map(asShelfItem).filter((x) => !!x && !x.hidden);
  const collections = shelf
    .map(asShelfCollection)
    .filter((x): x is ShelfCollection => !!x)
    .sort((a, b) => (a.sortIndex ?? a.addedAt) - (b.sortIndex ?? b.addedAt));
  const byId = (id: string) => works.find((w) => w.id === id) ?? null;
  const bibleById = (id: string) => bibles.find((b) => b.id === id) ?? null;
  const shown = items.filter((i) => !filter || i!.collectionIds.includes(filter));
  const recent = places
    .map(asPosition)
    .filter((p) => !!p)
    .sort((a, b) => b!.readAt - a!.readAt)
    .slice(0, 4);

  if (isPending) return null;
  const empty = !items.length && !recent.length;
  const onCase = view === "shelves";

  const mine: ReactNode[] = onCase
    ? []
    : shown.flatMap((i) => {
        const w = byId(i!.libraryId);
        const b = bibleById(i!.libraryId);
        if (b)
          return [
            <li key={i!.id}>
              <Link to="/bible/$id" params={{ id: b.id }} className={s.cover}>
                <BookCover id={b.id} title={b.title} shortTitle={b.shortTitle} width={132} caption />
              </Link>
            </li>,
          ];
        if (!w) return [];
        return [
          <li key={i!.id}>
            <BookLink work={w} st={state(w)} tone={toneOf(w, authors)} vt={`lib-mine-${w.id}`} />
          </li>,
        ];
      });

  // On the bookcase its books stand on the case's own My library rail (a region of that name), so this
  // head is not a second region by the same name.
  const Wrapper = onCase ? "div" : "section";
  const newCollectionButton = !naming && (
    <Button variant="text" size="sm" icon="folder" onClick={() => setNaming(true)}>
      New collection
    </Button>
  );
  return (
    <Wrapper className={onCase ? s.mineRow : s.section} aria-labelledby={onCase ? undefined : "mine-head"}>
      {onCase ? (
        // On the bookcase the head above says what the library is for and the case's own My library rail
        // holds the books: here only the collections that narrow it, and a new one.
        <div className={s.mineTools}>{newCollectionButton}</div>
      ) : (
        <SectionHeader id="mine-head" title="My library" action={newCollectionButton} />
      )}

      {naming && (
        <form
          className={s.nameForm}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            void change(newCollection(name));
            setName("");
            setNaming(false);
          }}
        >
          <label htmlFor="collection-name" className={ui.label}>
            Name the collection
          </label>
          <div className={s.nameRow}>
            <Input id="collection-name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              Make it
            </Button>
            <Button variant="text" onClick={() => setNaming(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {!onCase && recent.length > 0 && (
        <div className={s.continue}>
          <h3 className={s.subTitle}>Continue reading</h3>
          <ul className={s.places}>
            {recent.map((p) => {
              const bible = bibleById(p!.libraryId);
              const work = byId(p!.libraryId);
              const [bk, ch] = p!.anchor?.verseKey?.split(".") ?? [];
              const where = bk && ch ? `${bookByOsis(bk)?.ref ?? bk} ${ch}` : null;
              if (bible)
                return (
                  <li key={p!.id}>
                    <Link to="/bible/$id" params={{ id: bible.id }} className={s.place}>
                      <span className={s.leather} aria-hidden="true">
                        <span>{bible.shortTitle}</span>
                      </span>
                      <span className={s.rowText}>
                        <span className={s.name}>{bible.title}</span>
                        {where && <span className={s.meta}>{where}</span>}
                      </span>
                    </Link>
                  </li>
                );
              if (work && p!.anchor?.sectionUid)
                return (
                  <li key={p!.id}>
                    <Link to="/books/$id/$section" params={{ id: work.id, section: p!.anchor.sectionUid }} className={s.place}>
                      <BookCover id={work.id} title={work.title} shortTitle={work.shortTitle} author={work.author} tone={toneOf(work, authors)} width={36} decorative />
                      <span className={s.rowText}>
                        <span className={s.name}>{work.title}</span>
                        {p!.progress !== null && <span className={s.meta}>{Math.max(1, Math.round(p!.progress * 100))}% through</span>}
                      </span>
                    </Link>
                  </li>
                );
              return null;
            })}
          </ul>
        </div>
      )}

      {collections.length > 0 && (
        <nav className={s.chips} aria-label="Collections">
          <Link to="/library" search={{}} className={ui.chip} aria-current={!filter ? "page" : undefined}>
            Everything
          </Link>
          {collections.map((c) => (
            <Link key={c.id} to="/library" search={{ shelf: c.id }} className={ui.chip} aria-current={filter === c.id ? "page" : undefined}>
              {c.name}
            </Link>
          ))}
        </nav>
      )}

      {empty && onCase ? null : empty ? (
        <p className={s.emptyNote}>Add a book or a Bible from its page and it will stand here, with your place kept, on every device you sign in to.</p>
      ) : mine.length > 0 ? (
        <ul className={s.grid}>{mine}</ul>
      ) : (
        filter && !shown.length && <p className={s.emptyNote}>Nothing in this collection yet. Add works to it from their pages.</p>
      )}
    </Wrapper>
  );
}
